// The Dashboard PageHeader control strip: the leading board switcher (name
// select + copyable ID for API use), and the trailing range Select + author
// actions (Add widget / Edit layout, or Save/Cancel while a layout edit is in
// flight) + the board-management Menu. Every control renders sm with its Select
// label visually hidden so the whole header reads as ONE flush 32px row. Pure
// props — the route container owns every navigation and mutation.

import type { components } from "../api/schema";
import { RANGE_PRESET_LABELS, type RangePreset } from "../lib/dashboardMetrics";
import { shortResourceId } from "../lib/shellNavigation";
import type { DashboardBoardsApi } from "../lib/useDashboardBoards";
import { Button, Code, Copyable, PageHeader, Select } from "../ui";
import { DashboardBoardManager } from "./DashboardBoardManager";

type DashboardResponse = components["schemas"]["DashboardResponse"];

const RANGE_PRESETS: RangePreset[] = ["24h", "7d", "30d", "90d"];

// The PageHeader `leading` cluster: the board switcher shows the current
// dashboard's NAME (never its id) and the copyable short id sits beside it
// for API consumers.
export interface DashboardBoardSwitcherProps {
  agentId?: string;
  dashboardId: string | undefined;
  savedDashboards: DashboardResponse[];
  onSelectBoard: (boardId: string) => void;
}

export function DashboardBoardSwitcher({
  agentId,
  dashboardId,
  savedDashboards,
  onSelectBoard,
}: DashboardBoardSwitcherProps) {
  if (savedDashboards.length === 0 || !dashboardId) {
    return null;
  }
  return (
    <>
      <Select
        label="Board"
        labelHidden
        size="sm"
        value={dashboardId}
        onChange={(event) => {
          onSelectBoard(event.currentTarget.value);
        }}
      >
        {boardOptions(agentId, savedDashboards)}
      </Select>
      <Copyable label="Dashboard ID" value={dashboardId}>
        <Code>{shortResourceId(dashboardId)}</Code>
      </Copyable>
    </>
  );
}

// The whole Dashboard PageHeader strip: leading board switcher + trailing
// range/author controls. Kept here so the route container stays within its
// line budget and the header reads as one unit.
export function DashboardPageHeader(
  props: DashboardBoardSwitcherProps & DashboardHeaderControlsProps,
) {
  return (
    <PageHeader
      leading={
        <DashboardBoardSwitcher
          agentId={props.agentId}
          dashboardId={props.dashboardId}
          savedDashboards={props.savedDashboards}
          onSelectBoard={props.onSelectBoard}
        />
      }
      actions={<DashboardHeaderControls {...props} />}
    />
  );
}

export interface DashboardHeaderControlsProps {
  agentId?: string;
  boards: DashboardBoardsApi;
  canAuthor: boolean;
  isLayoutEditing: boolean;
  isSavePending: boolean;
  range: RangePreset;
  selectedDashboard: DashboardResponse | undefined;
  onAddWidget: () => void;
  onCancelLayoutEdit: () => void;
  onEnterLayoutEdit: () => void;
  onRangeChange: (range: RangePreset) => void;
  onSaveLayout: () => void;
}

export function DashboardHeaderControls({
  agentId,
  boards,
  canAuthor,
  isLayoutEditing,
  isSavePending,
  range,
  selectedDashboard,
  onAddWidget,
  onCancelLayoutEdit,
  onEnterLayoutEdit,
  onRangeChange,
  onSaveLayout,
}: DashboardHeaderControlsProps) {
  return (
    <>
      <Select
        label="Range"
        labelHidden
        size="sm"
        value={range}
        onChange={(event) => {
          onRangeChange(event.currentTarget.value as RangePreset);
        }}
      >
        {RANGE_PRESETS.map((preset) => (
          <option key={preset} value={preset}>
            {RANGE_PRESET_LABELS[preset]}
          </option>
        ))}
      </Select>
      {/* AFFORDANCE ONLY: member+ visibility comes from the client-side
          authority seam; every write remains server-authoritative and 403s
          surface as toasts. */}
      {canAuthor && selectedDashboard && !isLayoutEditing ? (
        <>
          <Button size="sm" variant="secondary" onClick={onAddWidget}>
            Add widget
          </Button>
          <Button size="sm" variant="secondary" onClick={onEnterLayoutEdit}>
            Edit layout
          </Button>
        </>
      ) : null}
      {canAuthor && isLayoutEditing ? (
        <>
          <Button disabled={isSavePending} size="sm" onClick={onSaveLayout}>
            Save layout
          </Button>
          <Button
            disabled={isSavePending}
            size="sm"
            variant="secondary"
            onClick={onCancelLayoutEdit}
          >
            Cancel
          </Button>
        </>
      ) : null}
      {canAuthor && !isLayoutEditing ? (
        <DashboardBoardManager
          agentId={agentId}
          boards={boards}
          selectedDashboard={selectedDashboard}
        />
      ) : null}
    </>
  );
}

// The switcher follows the sidebar's agent-scope model: with an agent
// selected, that agent's boards group first and organization-wide boards
// stay reachable below (the Markers built-ins precedent); with All agents,
// owned boards group separately from organization boards. Plain function
// (not a component) so the Select primitive's option parser sees the
// optgroup/option elements directly.
function boardOptions(
  agentId: string | undefined,
  boards: DashboardResponse[],
) {
  const owned = boards.filter(
    (board) => board.agent_id != null && board.agent_id === agentId,
  );
  const organization = boards.filter((board) => board.agent_id == null);
  const otherOwned = boards.filter(
    (board) => board.agent_id != null && board.agent_id !== agentId,
  );
  const option = (board: DashboardResponse) => (
    <option key={board.id} value={board.id}>
      {board.display_name}
    </option>
  );
  if (owned.length === 0 && otherOwned.length === 0) {
    return <>{organization.map(option)}</>;
  }
  return (
    <>
      {owned.length > 0 ? (
        <optgroup label="This agent">{owned.map(option)}</optgroup>
      ) : null}
      {organization.length > 0 ? (
        <optgroup label="Organization">{organization.map(option)}</optgroup>
      ) : null}
      {otherOwned.length > 0 ? (
        <optgroup label="Other agents">{otherOwned.map(option)}</optgroup>
      ) : null}
    </>
  );
}
